import React, { ReactNode, Suspense, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';

import { useClassnames } from '@lumapps/classnames';
import { isInDesignerMode as isInDesigner } from '@lumapps/contents/ducks/selectors';
import { useDataAttributes } from '@lumapps/data-attributes';
import { HeadingLevelProvider } from '@lumapps/lumx/react';
import { DisabledStateProvider } from '@lumapps/lumx/react/utils';
import { useSelector } from '@lumapps/redux/react';
import { useResponsive } from '@lumapps/responsive';
import { useTranslate } from '@lumapps/translations';
import { ErrorBoundary } from '@lumapps/utils/components/ErrorBoundary';
import { getCustomClasses } from '@lumapps/utils/string/getCustomClasses';
import { computeWidgetStyle } from '@lumapps/widget-styles/utils/computeWidgetStyle';

import { WidgetState } from '../../ducks/type';
import { WIDGET_BASE } from '../../keys';
import { Blocks, ContainerBlockVariant, isSingleContainerBlock, SlideshowBlockVariant, WidgetSizes } from '../../types';
import { getBlockMetaFromBody } from '../../utils/getBlockMetaFromBody';
import { getEffectiveWidgetType } from '../../utils/getEffectiveWidgetType';
import { getWidgetContentHtmlId, getWidgetHeaderHtmlId, getWidgetHtmlId } from '../../utils/getWidgetHtmlId';
import { BlockNoResults } from '../Block/BlockNoResults';
import { renderBlockTree } from '../Block/renderBlockTree';
import { EmptyWidgetPlaceholder } from '../EmptyWidgetPlaceholder';
import { WidgetContent, type WidgetContentProps } from '../WidgetContent';
import { WidgetFooter } from '../WidgetFooter';
import { WidgetHeader } from '../WidgetHeader';
import { getHeaderLabel } from '../WidgetHeader/getHeaderLabel';
import { WidgetSkeleton } from '../WidgetSkeleton';
import { useWidgetCollapse } from './useWidgetCollapse';

import './index.scss';

export interface WidgetProps {
    /** Widget state */
    widget: WidgetState;
    /** Widget content. */
    children?: ReactNode;
    /** The function called to fetch more widget blocks. */
    fetchMore?(): void;
    /** The function called to refetch the widget */
    refetch?(): void;
    /** Widget width size. */
    widgetWidth?: WidgetSizes;
    /** The component that will be used as a loader when the widget is loading. Default: WidgetSkeleton */
    loadingStateRenderer?: React.FC;
    /** The component that will be used as a placeholder when widget is empty. Default: none */
    emptyStateRenderer?: React.FC;
    /** The component that will be used as a placeholder when widget is error. Default: none */
    errorStateRenderer?: React.FC<{ onRefetch?: () => void }>;
    /** Optional component wrapping all the inside of the widget (header, content and footer) */
    wrapperInsideRenderer?: React.FC<{ children: React.ReactNode }>;
    /** Optional props passed down to the WidgetContent */
    widgetContentProps?: Pick<WidgetContentProps, 'blockProperties' | 'loadingMoreStateRenderer'>;
    /** Optional wrapper div props */
    wrapperProps?: Omit<React.ComponentProps<'div'>, 'children'>;
    /** Optional wrapper div props for loading state */
    loadingStateWrapperProps?: Omit<React.ComponentProps<'div'>, 'children'>;
}

export const Widget = React.forwardRef<HTMLElement, WidgetProps>((props, ref) => {
    const {
        fetchMore,
        refetch,
        widgetWidth,
        children,
        widgetContentProps,
        loadingStateRenderer: LoadingStateRenderer,
        emptyStateRenderer: EmptyStateRenderer,
        errorStateRenderer: ErrorStateRenderer,
        wrapperInsideRenderer: WrapperInsideRenderer = React.Fragment,
        widget,
        wrapperProps = {},
        loadingStateWrapperProps = {},
    } = props;
    // Use the legacy widget type when available.
    const widgetType = getEffectiveWidgetType(widget);
    const { more, header, footer, body, state, style, widgetId, isMainWidget, isCollapsed, htmlId, filters, cssClass } =
        widget;

    const { translateKey, translate } = useTranslate();
    const { isMobile } = useResponsive();
    const { block: blockClass } = useClassnames('widget');
    const { get: getDataAttributes } = useDataAttributes(`widget-${widgetType}`);

    const isInDesignerMode = useSelector(isInDesigner);

    const { isCurrentlyCollapsed, toggleCollapse } = useWidgetCollapse(isCollapsed && !isInDesignerMode, state);

    const cssClasses = useMemo(() => getCustomClasses(cssClass, 'widget--'), [cssClass]);

    /*
     * widgetId might be "undefined" if widget hasn't been migrated yet,
     * but it's necessary for a11y purposes. In that case, create a unique ID via lodash.
     */
    const uniqueWidgetId = useMemo(() => widgetId || uniqueId(), [widgetId]);

    const { isFullHeight, theme, widgetContentStyle, widgetStyle } = useMemo(
        () => computeWidgetStyle(style, body?.style, isMobile),
        [body, isMobile, style],
    );

    // Renders skeleton if loading or state not yet set.
    const skeleton = React.useMemo(() => {
        const defaultSkeleton = (
            <WidgetSkeleton ref={ref as any} widgetType={widgetType} widgetId={widgetId} htmlId={htmlId} />
        );

        const shouldBeWrapped = Boolean(LoadingStateRenderer || !isEmpty(loadingStateWrapperProps));

        return shouldBeWrapped ? (
            <div ref={ref as any} className={blockClass('is-loading')} {...loadingStateWrapperProps}>
                {LoadingStateRenderer ? <LoadingStateRenderer /> : defaultSkeleton}
            </div>
        ) : (
            defaultSkeleton
        );
    }, [LoadingStateRenderer, loadingStateWrapperProps, ref, blockClass, widgetType, widgetId, htmlId]);

    const EmptyState = React.useMemo(() => {
        if (EmptyStateRenderer) {
            return <EmptyStateRenderer />;
        }
        if (isInDesignerMode) {
            return (
                <EmptyWidgetPlaceholder
                    theme={theme}
                    widgetType={widgetType}
                    customPlaceholder={widget?.legacyWidget?.properties?.helper}
                    widgetContentStyle={widgetContentStyle}
                />
            );
        }
        return <BlockNoResults theme={theme} />;
    }, [
        EmptyStateRenderer,
        isInDesignerMode,
        theme,
        widget?.legacyWidget?.properties?.helper,
        widgetContentStyle,
        widgetType,
    ]);

    /*
     * Render widget for the following states: loading, empty and restricted.
     * Nb: Other states (error, not migrated, collapsed and nominal) will be treated during the content rendering.
     */
    // Restricted or empty widget should not render.
    if (state === 'restricted') {
        return null;
    }

    // Empty state should always be rendered when:
    // - In the designer
    // - No filters and the backend returns a BlockNoResults (wrapped in a container or not).
    const shouldForceEmptyWidgetRendering =
        isInDesignerMode ||
        (body?.type === 'BlockNoResults' && !filters) ||
        (!filters && body && isSingleContainerBlock(body) && body.container.type === 'BlockNoResults');

    if (state === 'empty' && !shouldForceEmptyWidgetRendering) {
        return null;
    }

    if ((widgetId || isInDesignerMode) && (!state || state === 'loading')) {
        return skeleton;
    }

    const hasHeader = Boolean(getHeaderLabel(header, translateKey));
    const hasFooter = footer?.label && (footer.resourceId || footer.url);

    const widgetHtmlId = htmlId || getWidgetHtmlId(uniqueWidgetId);
    const widgetContentHtmlId = getWidgetContentHtmlId(uniqueWidgetId);
    const widgetHeaderHtmlId = getWidgetHeaderHtmlId(uniqueWidgetId);

    const block = getBlockMetaFromBody(body as Blocks);

    const hasUngroupedChildren = block?.variant === ContainerBlockVariant.ungrouped;
    const isCoverViewMode = block?.variant === SlideshowBlockVariant.cover;
    const isSideViewMode = block?.variant === SlideshowBlockVariant.side;
    const isListViewMode = block?.arrayContainer?.type === 'BlockList';
    const isGridViewMode =
        block?.arrayContainer?.type === 'BlockGrid' ||
        (block?.arrayContainer?.type === 'BlockSlideshow' && !isCoverViewMode && !isSideViewMode);
    const isCascadeViewMode = block?.arrayContainer?.type === 'BlockCascade';
    const hasSlideshow = block?.arrayContainer?.type === 'BlockSlideshow';
    const hasTabs = block?.type === 'BlockTabFilteredContainer';

    let content;

    if (children) {
        content = children;
    } else if (!widgetId || state === 'notmigrated') {
        content = <BlockNoResults theme={theme} placeholderText={translate(WIDGET_BASE.NOT_MIGRATED) as string} />;
    } else if (state === 'error') {
        content = ErrorStateRenderer ? (
            <ErrorStateRenderer onRefetch={refetch} />
        ) : (
            <BlockNoResults theme={theme} placeholderText={translate(WIDGET_BASE.COULD_NOT_LOAD) as string} />
        );
    } else if (state === 'empty') {
        content = EmptyState;
    } else {
        content = renderBlockTree(widget) || EmptyState;
    }

    const widgetContent = (
        <WidgetContent
            id={widgetContentHtmlId}
            style={widgetContentStyle}
            onLoadMore={more ? fetchMore : undefined}
            isCollapsed={Boolean(isCurrentlyCollapsed)}
            blockVariant={block?.variant}
            blockType={block?.type}
            widgetId={uniqueWidgetId}
            widgetType={widgetType}
            isMainWidget={isMainWidget}
            loadingState={state}
            paginationType={widget.paginationType}
            htmlId={htmlId}
            widgetRef={ref}
            {...widgetContentProps}
        >
            {content}
        </WidgetContent>
    );

    let wrappedContent = hasHeader ? <HeadingLevelProvider>{widgetContent}</HeadingLevelProvider> : widgetContent;
    if (isInDesignerMode) {
        // Nested content is disabled in designer mode
        wrappedContent = <DisabledStateProvider state="disabled">{wrappedContent}</DisabledStateProvider>;
    }
    return (
        <Suspense fallback={skeleton}>
            <div
                {...wrapperProps}
                {...getDataAttributes({ element: uniqueWidgetId })}
                id={widgetHtmlId}
                data-widget-id={widgetId}
                data-widget-type={widgetType}
                className={blockClass(
                    {
                        [`${widgetWidth}`]: !!widgetWidth,
                        [`theme-${theme}`]: !!theme,
                        'is-full-height': !!isFullHeight && !isCurrentlyCollapsed,
                        'is-folded': !!isCurrentlyCollapsed,
                        'is-collapsible': !!isCollapsed,
                        'has-header': hasHeader,
                        'has-footer': !!hasFooter,
                        'has-ungrouped-container-block': hasUngroupedChildren,
                        'view-mode-cover': isCoverViewMode,
                        'view-mode-side': isSideViewMode,
                        'view-mode-list': isListViewMode,
                        'view-mode-grid': isGridViewMode,
                        'view-mode-cascade': isCascadeViewMode,
                        'variant-slideshow': hasSlideshow,
                        'variant-tabs': hasTabs,
                        'variant-ungrouped': hasUngroupedChildren,
                        'variant-grouped': !hasUngroupedChildren,
                        'is-refreshing': state === 'refreshing',
                    },
                    [`widget-${widgetType}`, ...cssClasses, wrapperProps?.className],
                )}
                aria-busy={state === 'refreshing'}
                style={widgetStyle}
                ref={ref as React.RefObject<HTMLDivElement>}
            >
                <WrapperInsideRenderer>
                    {hasHeader && (
                        <WidgetHeader
                            {...header}
                            parentStyle={style}
                            id={widgetHeaderHtmlId}
                            onCollapse={isCollapsed && !isInDesignerMode ? toggleCollapse : undefined}
                            aria-expanded={!isCurrentlyCollapsed}
                            aria-controls={widgetContentHtmlId}
                            isStandalone={!hasFooter && isCurrentlyCollapsed}
                        />
                    )}

                    <ErrorBoundary
                        fallback={
                            ErrorStateRenderer ? (
                                <ErrorStateRenderer onRefetch={refetch} />
                            ) : (
                                <BlockNoResults
                                    theme={theme}
                                    placeholderText={translateKey(WIDGET_BASE.COULD_NOT_LOAD) as string}
                                />
                            )
                        }
                    >
                        {wrappedContent}
                    </ErrorBoundary>

                    {hasFooter && <WidgetFooter parentStyle={style} {...footer} />}
                </WrapperInsideRenderer>
            </div>
        </Suspense>
    );
});
Widget.displayName = 'Widget';
